// HerSpace Hub — Daily prompts, stories, reactions, and community reflections
import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import { Loader2, RefreshCw, Send, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../api/client';
import { AuthContext } from '../App';
import {
  HERSPACE_REACTION_TYPES,
} from '../utils/herSpacePrompts';
import { useRoomPromptsToday } from '../hooks/useRoomPromptsToday';

function formatTime(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function HerSpaceHub({ activeChannelKey, activeChannelLabel, onBack }) {
  const { token } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${token}` };

  const [posts, setPosts] = useState([]);
  const [topPosts, setTopPosts] = useState([]);
  const [reactionState, setReactionState] = useState({});
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);

  // Room-specific prompt from backend (replaces shared static prompt)
  const { getFullPromptForRoom } = useRoomPromptsToday();
  const promptRecord = useMemo(() => getFullPromptForRoom(activeChannelKey), [getFullPromptForRoom, activeChannelKey]);
  const dailyPrompt = promptRecord?.prompt_text || "Share what's been on your heart today.";
  const promptDayKey = promptRecord?.date || new Date().toISOString().split('T')[0];
  const promptBadge = promptRecord?.theme_badge || "Today's Prompt";
  const storyPlaceholder = "Share a short story, reflection, or experience inspired by today's prompt...";

  const loadPosts = useCallback(async () => {
    try {
      const response = await apiClient.get('/herspace/posts', {
        params: { channel_key: activeChannelKey },
        headers,
      });
      setPosts(Array.isArray(response?.data?.posts) ? response.data.posts : []);
    } catch (err) {
      console.error('Failed to load posts', err);
    }
  }, [activeChannelKey, token]);

  const loadTopPosts = useCallback(async () => {
    try {
      const response = await apiClient.get('/herspace/reflections', {
        params: { channel_key: activeChannelKey, limit: 3 },
        headers,
      });
      setTopPosts(Array.isArray(response?.data?.posts) ? response.data.posts : []);
    } catch (err) {
      console.error('Failed to load reflections', err);
      setTopPosts([]);
    }
  }, [activeChannelKey, token]);

  const loadReactionState = useCallback(async () => {
    try {
      const response = await apiClient.get('/herspace/reactions/my', {
        params: { channel_key: activeChannelKey },
        headers,
      });
      const entries = Array.isArray(response?.data?.reactions) ? response.data.reactions : [];
      const nextState = {};
      entries.forEach((entry) => {
        if (!entry?.post_id || !entry?.reaction_type) return;
        if (!nextState[entry.post_id]) nextState[entry.post_id] = {};
        nextState[entry.post_id][entry.reaction_type] = true;
      });
      setReactionState(nextState);
    } catch (err) {
      console.error('Failed to load reaction state', err);
      setReactionState({});
    }
  }, [activeChannelKey, token]);

  const reactToPost = async (postId, reactionType) => {
    try {
      await apiClient.post('/herspace/react', {
        post_id: postId,
        reaction_type: reactionType,
      }, { headers });

      setReactionState((prev) => ({
        ...prev,
        [postId]: { ...(prev[postId] || {}), [reactionType]: true },
      }));

      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.id === postId
            ? {
                ...post,
                reaction_counts: {
                  ...(post.reaction_counts || {}),
                  [reactionType]: (post.reaction_counts?.[reactionType] || 0) + 1,
                },
              }
            : post
        )
      );
    } catch (err) {
      console.error('Failed to react', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!draft.trim() || submitting) return;
    setSubmitting(true);
    try {
      await apiClient.post('/herspace/posts', {
        channel_key: activeChannelKey,
        body: draft.trim(),
        prompt_day_key: promptDayKey,
        prompt_text: dailyPrompt,
      }, { headers });
      setDraft('');
      await Promise.all([loadPosts(), loadTopPosts()]);
      toast.success('Shared!');
    } catch (err) {
      toast.error('Failed to share');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadPosts(), loadTopPosts(), loadReactionState()]);
    setRefreshing(false);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([loadPosts(), loadTopPosts(), loadReactionState()]).finally(() => setLoading(false));
  }, [activeChannelKey]);

  function getReactionCount(post, reactionType) {
    return post?.reaction_counts?.[reactionType] || 0;
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-rose-500" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition"
              data-testid="hub-back-btn">
              <ArrowLeft className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
          )}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{activeChannelLabel}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Stories & reflections</p>
          </div>
        </div>
        <button onClick={handleRefresh} disabled={refreshing}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition disabled:opacity-50"
          data-testid="hub-refresh-btn">
          <RefreshCw className={`w-4 h-4 text-gray-500 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Daily Prompt */}
      <div className="surface-card rounded-2xl p-5 shadow-sm mb-5 border-l-4 border-rose-400 dark:border-rose-500"
        data-testid="daily-prompt-card">
        <div className="text-xs font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400 mb-2">
          {promptBadge}
        </div>
        <p className="text-sm text-gray-900 dark:text-white font-medium leading-relaxed mb-2">
          {dailyPrompt}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-500">
          Stories help us learn from each other. Speak from experience, not judgment.
        </p>
      </div>

      {/* Top Reflections */}
      {topPosts.length > 0 && (
        <div className="surface-card rounded-2xl p-5 shadow-sm mb-5" data-testid="reflections-card">
          <div className="text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-1">
            Community Reflections
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Community Pulse</p>
          <div className="space-y-3">
            {topPosts.map((post) => (
              <div key={post.id} className="px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-slate-800/60">
                <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {post.author_name || 'Community member'}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-3">{post.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compose */}
      <form onSubmit={handleSubmit} className="surface-card rounded-2xl p-5 shadow-sm mb-5" data-testid="compose-form">
        <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">Share a thought</div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Keep it respectful. Report anything off.</p>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={storyPlaceholder}
          maxLength={5000}
          rows={3}
          className="w-full px-4 py-3 text-sm rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-300 dark:focus:ring-rose-500/40 resize-none mb-3"
          data-testid="compose-textarea"
        />
        <div className="flex justify-end">
          <button type="submit" disabled={submitting || !draft.trim()}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-xl bg-rose-600 text-white hover:bg-rose-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="compose-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Share
          </button>
        </div>
      </form>

      {/* Post List */}
      <div className="space-y-3" data-testid="post-list">
        {posts.length === 0 ? (
          <div className="text-center py-10 text-gray-400 dark:text-gray-500">
            <p className="text-sm">No posts in this channel yet. Be the first to share!</p>
          </div>
        ) : (
          posts.map((post) => (
            <article key={post.id} className="surface-card rounded-2xl p-4 shadow-sm" data-testid={`post-${post.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center text-xs font-bold text-rose-700 dark:text-rose-300">
                  {(post.author_name || '?')[0].toUpperCase()}
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{post.author_name || 'Community member'}</span>
                <span className="text-xs text-gray-400">{formatTime(post.created_at)}</span>
              </div>

              {post.prompt_day_key === promptDayKey && (
                <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 mb-2"
                  data-testid="story-of-day-tag">
                  {promptBadge}
                </span>
              )}

              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-3 whitespace-pre-wrap">{post.body}</p>

              {/* Reactions */}
              <div className="flex gap-2" data-testid={`reactions-${post.id}`}>
                {HERSPACE_REACTION_TYPES.map((reaction) => {
                  const selected = !!reactionState?.[post.id]?.[reaction.key];
                  const count = getReactionCount(post, reaction.key);
                  return (
                    <button
                      key={reaction.key}
                      type="button"
                      onClick={() => reactToPost(post.id, reaction.key)}
                      disabled={selected}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full transition ${
                        selected
                          ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 cursor-default'
                          : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700'
                      }`}
                      data-testid={`reaction-${reaction.key}-${post.id}`}
                    >
                      <span>{reaction.emoji}</span>
                      <span>{reaction.label}</span>
                      {count > 0 && <span className="font-medium">{count}</span>}
                    </button>
                  );
                })}
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}
